import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  ArrowLeft, UserPlus, Search, Edit2, Trash2, Shield, 
  ChevronLeft, ChevronRight, RefreshCw,
  Mail, Phone, Building, User, AlertCircle
} from 'lucide-react'
import { userApi } from '@/lib/api/users'
import { INVITATIONS_QUERY_KEY, type InvitationIssued } from '@/lib/api/invitations'
import { InviteUserModal } from './InviteUserModal'
import { PendingInvitations } from './PendingInvitations'
import { UserEditModal } from './UserEditModal'
import { useAuth } from '@/contexts/AuthContext'
import toast from 'react-hot-toast'

interface UserManagementProps {
  onBack: () => void
}

export function UserManagement({ onBack }: UserManagementProps) {
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedRole, setSelectedRole] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [resent, setResent] = useState<InvitationIssued | null>(null)
  const [editingUser, setEditingUser] = useState<any>(null)
  
  const pageSize = 10
  
  // Fetch users
  const { data: usersData, isLoading, refetch } = useQuery({
    queryKey: ['users', currentPage, searchQuery, selectedRole, selectedStatus],
    queryFn: () => userApi.getUsers({
      page: currentPage,
      page_size: pageSize,
      search: searchQuery || undefined,
      role: selectedRole !== 'all' ? selectedRole : undefined,
      is_active: selectedStatus === 'all' ? undefined : selectedStatus === 'active'
    })
  })
  
  // Delete user mutation
  const deleteMutation = useMutation({
    mutationFn: (userId: string) => userApi.deleteUser(userId),
    onSuccess: () => {
      toast.success('User deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete user')
    }
  })
  
  // Toggle user status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) =>
      userApi.updateUser(userId, { is_active: !isActive }),
    onSuccess: () => {
      toast.success('User status updated')
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update user status')
    }
  })
  
  const handleDeleteUser = (userId: string) => {
    if (confirm('Are you sure you want to delete this user?')) {
      deleteMutation.mutate(userId)
    }
  }
  
  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
      case 'admin':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
    }
  }
  
  const formatRole = (role: string) => {
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1f5c] transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              User Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage users, roles, and permissions
            </p>
          </div>
        </div>
        
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#5869ff] text-white rounded-lg hover:bg-[#4757e6] transition-colors"
        >
          <UserPlus className="w-5 h-5" />
          <span>Invite User</span>
        </button>
      </div>
      
      {/* Filters */}
      <div className="bg-white dark:bg-[#000742]/50 rounded-xl border border-gray-200 dark:border-[#5869ff]/20 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or department..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#5869ff] focus:border-transparent"
            />
          </div>
          
          {/* Role Filter */}
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#5869ff] focus:border-transparent"
          >
            <option value="all">All Roles</option>
            <option value="super_admin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="viewer">Viewer</option>
          </select>
          
          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#5869ff] focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          
          {/* Refresh Button */}
          <button
            onClick={() => refetch()}
            className="p-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 hover:bg-gray-100 dark:hover:bg-[#1a1f5c] transition-colors"
          >
            <RefreshCw className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>
      
      {/* Users Table */}
      <div className="bg-white dark:bg-[#000742]/50 rounded-xl border border-gray-200 dark:border-[#5869ff]/20 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 text-[#5869ff] animate-spin" />
          </div>
        ) : usersData?.users && usersData.users.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-[#1a1f5c]/30 border-b border-gray-200 dark:border-[#5869ff]/20">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-[#5869ff]/10">
                  {usersData.users.map((user: any) => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-[#1a1f5c]/20 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#5869ff]/10 flex items-center justify-center">
                            <User className="w-5 h-5 text-[#5869ff]" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {user.full_name || `${user.first_name} ${user.last_name}`}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {user.job_title || 'No title'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white flex items-center gap-1">
                          <Mail className="w-4 h-4 text-gray-400" />
                          {user.email}
                        </div>
                        {user.phone && (
                          <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <Phone className="w-4 h-4 text-gray-400" />
                            {user.phone}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                          <Shield className="w-3 h-3" />
                          {formatRole(user.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white flex items-center gap-1">
                          <Building className="w-4 h-4 text-gray-400" />
                          {user.department || 'Not assigned'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleStatusMutation.mutate({ userId: user.id, isActive: user.is_active })}
                          disabled={user.id === currentUser?.id}
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full transition-colors ${
                            user.is_active
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                          } ${user.id === currentUser?.id ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:opacity-80'}`}
                        >
                          {user.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {user.last_login_at 
                          ? new Date(user.last_login_at).toLocaleDateString()
                          : 'Never'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setEditingUser(user)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1f5c] transition-colors"
                          >
                            <Edit2 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            disabled={user.id === currentUser?.id}
                            className={`p-1.5 rounded-lg transition-colors ${
                              user.id === currentUser?.id
                                ? 'cursor-not-allowed opacity-50'
                                : 'hover:bg-red-100 dark:hover:bg-red-900/30'
                            }`}
                          >
                            <Trash2 className={`w-4 h-4 ${
                              user.id === currentUser?.id
                                ? 'text-gray-400'
                                : 'text-red-600 dark:text-red-400'
                            }`} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {usersData.total_pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-[#5869ff]/20">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-gray-400">
                    Showing {((currentPage - 1) * pageSize) + 1} to{' '}
                    {Math.min(currentPage * pageSize, usersData.total)} of{' '}
                    {usersData.total} users
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 hover:bg-gray-100 dark:hover:bg-[#1a1f5c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    
                    {Array.from({ length: Math.min(5, usersData.total_pages) }, (_, i) => {
                      const pageNum = i + 1
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1 rounded-lg transition-colors ${
                            currentPage === pageNum
                              ? 'bg-[#5869ff] text-white'
                              : 'hover:bg-gray-100 dark:hover:bg-[#1a1f5c] text-gray-700 dark:text-gray-400'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(usersData.total_pages, prev + 1))}
                      disabled={currentPage === usersData.total_pages}
                      className="p-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 hover:bg-gray-100 dark:hover:bg-[#1a1f5c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
            <AlertCircle className="w-12 h-12 mb-2" />
            <p>No users found</p>
          </div>
        )}
      </div>
      
      {/* Pending invitations */}
      <PendingInvitations onResent={setResent} />
      
      {/* Modals */}
      {(showInviteModal || resent) && (
        <InviteUserModal
          initial={resent}
          onClose={() => {
            setShowInviteModal(false)
            setResent(null)
          }}
          onIssued={() => queryClient.invalidateQueries({ queryKey: INVITATIONS_QUERY_KEY })}
        />
      )}
      
      {editingUser && (
        <UserEditModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            setEditingUser(null)
            queryClient.invalidateQueries({ queryKey: ['users'] })
          }}
        />
      )}
    </div>
  )
}